import { Component, inject, ViewChild,
    ChangeDetectionStrategy
} from '@angular/core';
import { MenuItem } from '@openng/optimus-ui/api';
import { ComponentView } from 'src/modules/app-template/models/component-view.model';
import { ChangeSummary } from 'src/modules/enaio/netgen/ChangeSummary';
import { SmTableComponent } from 'src/modules/sm-base/components/sm-table/sm-table.component';
import { TableCellButton } from 'src/modules/sm-base/models/table-cell-button.model';
import { TableCellEvent } from 'src/modules/sm-base/models/table-cell-event.model';
import { TableData } from 'src/modules/sm-base/models/table-data.model';
import { TableRow } from 'src/modules/sm-base/models/table-row.model';
import { FrontendField } from 'src/modules/sm-base/netgen/FrontendField';
import { FrontendFieldListItem } from 'src/modules/sm-base/netgen/FrontendFieldListItem';
import { FrontendFieldType } from 'src/modules/sm-base/netgen/FrontendFieldType';
import { FrontendForm } from 'src/modules/sm-base/netgen/FrontendForm';
import { TableCellType } from 'src/modules/sm-base/netgen/TableCellType';
import { TableColumn } from 'src/modules/sm-base/netgen/TableColumn';
import { Arrays } from 'src/modules/utils/shared/arrays';
import { Objects } from 'src/modules/utils/shared/objects';
import { Utils } from 'src/modules/utils/shared/utils';
import { ProjectDto } from '../../netgen/ProjectDto';
import { ProjectType } from '../../netgen/ProjectType';
import { ProjectPlanningService } from '../../services/project-planning.service';
import { ProjectPlanningMainComponent } from '../project-planning-main/project-planning-main.component';

@Component({
    selector: 'app-planning-view-project-overview',
    templateUrl: './planning-view-project-overview.component.html',
    styleUrl: './planning-view-project-overview.component.scss',
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class PlanningViewProjectOverviewComponent extends ComponentView {

    service = inject(ProjectPlanningService);
        
    projects: ProjectDto[];
    projectTypes: ProjectType[];
    milestones: string[];
    form: FrontendForm;
    table: TableData;
    selectedProject: ProjectDto;

    @ViewChild("tableComponent")
    tableComponent: SmTableComponent;

    async initParams(): Promise<boolean> {
        this.form = new FrontendForm([
            new FrontendField("onlyWithDeliveryDate", this.app.t("ProjectPlanning.onlyWithDeliveryDate"), FrontendFieldType.bool, {
                value: true,
                onValueChanged: this.fillTable.bind(this)
            }),
            new FrontendField("finished", this.app.t("ProjectPlanning.alsoFinishedProjects"), FrontendFieldType.bool, {
                onValueChanged: this.fillTable.bind(this)
            }),
            new FrontendField("projectTypes", this.app.t("ProjectPlanning.showProjectTypes"), FrontendFieldType.dropdownMulti, {
                onValueChanged: this.fillTable.bind(this)
            }),
            new FrontendField("milestones", this.app.t("ProjectPlanning.showMilestones"), FrontendFieldType.dropdownMulti, {
                onValueChanged: this.fillTable.bind(this),
                value: []
            }),
            new FrontendField("showProjectStaff", this.app.t("ProjectPlanning.showProjectStaff"), FrontendFieldType.bool, {
                value: false,
                onValueChanged: () => this.projectChanged(true)
            }),
            new FrontendField("selectedProjectStaff", this.app.t("ProjectPlanning.filterByStaff"), FrontendFieldType.dropdownMulti, {
                onValueChanged: () => this.projectChanged(true),
                value: []
            })
        ]);

        await this.service.getProjectTypes();
        await this.projectChanged(false);

        this.form.getField("projectTypes").setValue(this.projectTypes.map(pt => pt.id));
        
        this.fillTable();

        this.addSubscription(this.app.registerChangesCallback(this.serviceChanged.bind(this), "PlanningViewProjectOverview"));

        return true;
    }

    async serviceChanged(summary: ChangeSummary): Promise<void> {
        if (summary.hasAnyType(["Project", "ProjectType", "CalendarEvent", "CalendarEventRole"])) {
            await this.projectChanged(true);
        }           
    }

    async projectChanged(fillTable = true): Promise<void> {
        this.projectTypes = await this.service.getProjectTypes();
        let includeEvents = this.form.getValueBool("showProjectStaff") || !Utils.isNoe(this.form.getValue("selectedProjectStaff") as number[]);
        this.projects = await this.service.getProjectDtos(includeEvents);
        this.milestones = Arrays.sort(Arrays.getUnique(Arrays.explode(this.projects, p => Objects.getPropertyNames(p.stats.milestoneDates))));

        this.form.getField("projectTypes").listItems = FrontendFieldListItem.make(this.projectTypes, item => [item.id, item.title]);
        this.form.getField("milestones").listItems = FrontendFieldListItem.make(this.milestones);
        this.form.getField("selectedProjectStaff").listItems = FrontendFieldListItem.make(await this.service.getActiveUsers(), item => [item.id, item.getFullName()]);

        if (fillTable) {
            this.fillTable();
            this.selectedProject = this.selectedProject != null ? await this.service.getProjectDto(this.selectedProject?.project.id) : null;
        }
    }
    
    fillTable(): void {
        let milestones = this.form.getValue("milestones") as string[];
        let projectTypes = this.form.getValue("projectTypes") as number[];
        let selectedProjectStaff = this.form.getValue("selectedProjectStaff") as number[];
        let finished = this.form.getValueBool("finished");
        let onlyWithDeliveryDate = this.form.getValueBool("onlyWithDeliveryDate");
        let projects = finished ? this.projects : this.projects.filter(p => !p.project.finished);
        if (!Utils.isNoe(projectTypes)) {
            projects = projects.filter(p => projectTypes.includes(p.project.typeId));
        }
        if (onlyWithDeliveryDate) {
            projects = projects.filter(p => p.project.deliveryDate1 != null);
        }
        if (!Utils.isNoe(selectedProjectStaff)) {
            projects = projects.filter(p => selectedProjectStaff.some(userId => p.isEditor(userId)));
        }
        let anyDeliveryDate2 = projects.some(p => p.project.deliveryDate2 != null);
        let anyDeliveryDate3 = projects.some(p => p.project.deliveryDate3 != null);
        let showProjectStaff = this.form.getValueBool("showProjectStaff");
        this.table = TableData.update(this.table, [
            new TableColumn("action", "", TableCellType.button, 1),
            new TableColumn("projectNumber", this.app.t("entity.Project.fields.projectNumber"), TableCellType.string, 15),
            new TableColumn("title", this.app.t("ProjectPlanning.projectTitle"), TableCellType.string, 30),
            new TableColumn("donePercentage", this.app.t("ProjectPlanning.donePercentage"), TableCellType.int, 5, {
                valueFormatter: e => Utils.formatNumber(e.value as number, 2, true) + "%"
            }) ,              
            new TableColumn("timePercentage", this.app.t("ProjectPlanning.timePercentage"), TableCellType.int, 5, {
                valueFormatter: e => Utils.formatNumber(e.value as number, 2, true) + "%"
            }),
            new TableColumn("projectType", this.app.t("entity.Project.fields.typeId"), TableCellType.string, 15),
            new TableColumn("startDate", this.app.t("entity.Project.fields.startDate"), TableCellType.date, 1, {
                align: "center"
            }),
            new TableColumn("deliveryDate1", this.app.t("ProjectPlanning.deliveryDate1"), TableCellType.date, 1, {
                align: "center"
            }),
            anyDeliveryDate2 ? new TableColumn("deliveryDate2", this.app.t("ProjectPlanning.deliveryDate2"), TableCellType.date, 1, {
                align: "center"
            }) : null,
            anyDeliveryDate3 ? new TableColumn("deliveryDate3", this.app.t("ProjectPlanning.deliveryDate3"), TableCellType.date, 1, {
                align: "center"
            }) : null,
            new TableColumn("endDate", this.app.t("ProjectPlanning.endDate"), TableCellType.date, 1, {
                align: "center"
            }),
            new TableColumn("plannedTime", this.app.t("entity.CalendarEvent.fields.plannedTime"), TableCellType.decimal, 5, {
                numDecimalDigits: 2,
                expandDecimalDigits: true,
                align: "center"
            }),                
            new TableColumn("doneTime", this.app.t("entity.CalendarEvent.fields.doneTime"), TableCellType.decimal, 5, {
                numDecimalDigits: 2,
                expandDecimalDigits: true,
                align: "center"
            }),                
            ...showProjectStaff ? [new TableColumn("projectStaff", "Projektmitarbeiter", TableCellType.string, 20)] : [],
            ...milestones.map(ms => new TableColumn("ms_" + ms, ms, TableCellType.date, 1, {
                align: "center",
                useHeaderWidthAsMin: false
            })),
            finished ? new TableColumn("status", this.app.t("ProjectPlanning.status"), TableCellType.string, 5) : null
        ], projects.map(p => new TableRow(Objects.toString(p.project.id), p, {
            action: [new TableCellButton("openProject", "fas fa-arrow-right-from-bracket", this.app.t("ProjectPlanning.openProjectInView1"))],
            projectNumber: p.project.projectNumber,
            title: p.project.title,
            donePercentage: p.stats.donePercentage,
            timePercentage: p.stats.timePercentage,
            projectType: this.service.getProjectType(p.project.typeId)?.title ?? "–",
            startDate: p.project.startDate?.toDate(),
            deliveryDate1: p.project.deliveryDate1?.toDate(),
            deliveryDate2: p.project.deliveryDate2?.toDate(),
            deliveryDate3: p.project.deliveryDate3?.toDate(),
            endDate: p.stats.endDate,
            plannedTime: p.stats.plannedTime,
            doneTime: p.stats.doneTime,
            ...showProjectStaff ? { projectStaff: p.getAllEditorsText(this.service) } : {},
            ...Arrays.toOrdinaryObject(milestones, ms => "ms_" + ms, ms => p.stats.milestoneDates[ms]),
            status: p.project.finished ? this.app.t("ProjectPlanning.statusFinished") : this.app.t("ProjectPlanning.statusActive")            
        }, { 
            getContextMenuItems: this.getContextMenuItems.bind(this)
        })), ["deliveryDate1", "startDate"]);

    }

    getContextMenuItems(e: TableCellEvent): MenuItem[] {
        return this.service.getProjectContextMenuItems((e.row.raw as ProjectDto).project, null, this.projectChanged.bind(this), this.projectChanged.bind(this), this);
    }

    async selectRow(row: TableRow): Promise<void> {
        this.selectedProject = await this.service.getProjectDto((row.raw as ProjectDto).project.id);
    }

    clickCell(event: TableCellEvent): void {
        if (event.isButton("openProject", "action")) {
            let p = event.row.raw as ProjectDto;
            ProjectPlanningMainComponent.instance.openProjectInView1(p.project);
        }
    }
}
